import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgInputDate, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';

import type { IncidentArchiveFilters } from './filters';
import { defaultIncidentArchiveFilters, sanitizeFilters } from './filters';
import { IncidentArchiveFiltersSummary } from './IncidentArchiveFiltersSummary';

export type IncidentArchiveActionBoxProps = {
  filters?: IncidentArchiveFilters | null;
  filtersApplied: (filters: IncidentArchiveFilters) => void | Promise<void>;
};

const INLINE_FIELD_ID_PREFIX = 'filter';
const DIALOG_FIELD_ID_PREFIX = 'filterDialog';

export function IncidentArchiveActionBox({ filters, filtersApplied }: IncidentArchiveActionBoxProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<NgModalHandle>(null);
  const dialogFormId = useId();

  const [filtersModel, setFiltersModel] = useState<IncidentArchiveFilters>(
    () => filters ?? defaultIncidentArchiveFilters(),
  );
  useEffect(() => {
    setFiltersModel(filters ?? defaultIncidentArchiveFilters());
  }, [filters]);

  const editForm = useEditForm<'phoneNumber' | 'callerName' | 'note'>({
    initialBaselineValues: {
      phoneNumber: filtersModel.phoneNumber ?? '',
      callerName: filtersModel.callerName ?? '',
      note: filtersModel.note ?? '',
    },
  });

  const applyFilters = () => {
    dialogRef.current?.hide();

    const sanitized = sanitizeFilters(filtersModel);
    setFiltersModel(sanitized);
    void filtersApplied(sanitized);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    editForm.validate(() => ({}));
  };

  const renderFilterForm = (idPrefix: string, formId?: string) => {
    const phoneNumberId = `${idPrefix}PhoneNumber`;
    const callerNameId = `${idPrefix}CallerName`;
    const noteId = `${idPrefix}Note`;

    return (
      <form id={formId} onSubmit={handleSubmit}>
        <div className="d-flex gap-2 flex-column flex-tablet-row align-items-stretch align-items-tablet-end">
          <NgInputDate
            value={filtersModel.from}
            onValueChanged={(date) => setFiltersModel((model) => ({ ...model, from: date }))}
            label={t('IncidentArchive_Filters_DateFrom')}
            maxDate={filtersModel.to ?? undefined}
          />

          <NgInputDate
            value={filtersModel.to}
            onValueChanged={(date) => setFiltersModel((model) => ({ ...model, to: date }))}
            label={t('IncidentArchive_Filters_DateTo')}
            minDate={filtersModel.from ?? undefined}
          />

          <div>
            <label htmlFor={phoneNumberId} className="form-label">
              {t('IncidentArchive_Filters_PhoneNumber')}
            </label>
            <input
              value={filtersModel.phoneNumber ?? ''}
              onChange={(event) => {
                setFiltersModel((model) => ({ ...model, phoneNumber: event.target.value }));
                editForm.notifyFieldInput('phoneNumber', event.target.value);
              }}
              onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
              id={phoneNumberId}
              className={editForm.fieldCssClass('phoneNumber', 'form-control')}
            />
          </div>

          <div>
            <label htmlFor={callerNameId} className="form-label">
              {t('IncidentArchive_Filters_CallerName')}
            </label>
            <input
              value={filtersModel.callerName ?? ''}
              onChange={(event) => {
                setFiltersModel((model) => ({ ...model, callerName: event.target.value }));
                editForm.notifyFieldInput('callerName', event.target.value);
              }}
              onBlur={() => editForm.notifyFieldBlur('callerName')}
              id={callerNameId}
              className={editForm.fieldCssClass('callerName', 'form-control')}
            />
          </div>

          <div>
            <label htmlFor={noteId} className="form-label">
              {t('IncidentArchive_Filters_DispatcherNote')}
            </label>
            <input
              value={filtersModel.note ?? ''}
              onChange={(event) => {
                setFiltersModel((model) => ({ ...model, note: event.target.value }));
                editForm.notifyFieldInput('note', event.target.value);
              }}
              onBlur={() => editForm.notifyFieldBlur('note')}
              id={noteId}
              className={editForm.fieldCssClass('note', 'form-control')}
            />
          </div>

          {formId === undefined && (
            <NgButton submitButton text={t('IncidentArchive_Filters_FilterButtonLabel')} onClick={applyFilters} />
          )}
        </div>
      </form>
    );
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-link text-body text-decoration-none d-flex d-tablet-none align-items-center justify-content-between gap-2 w-100 p-2 bg-light"
        onClick={() => dialogRef.current?.show()}
      >
        <i className="fal fa-filter"></i>

        <IncidentArchiveFiltersSummary filters={filtersModel} />

        <i className="fal fa-chevron-down"></i>
      </button>

      <div className="d-none d-tablet-block">{renderFilterForm(INLINE_FIELD_ID_PREFIX)}</div>

      <NgModal
        ref={dialogRef}
        header={t('IncidentArchive_Filters_FilterButtonLabel')}
        bodyTemplate={renderFilterForm(DIALOG_FIELD_ID_PREFIX, dialogFormId)}
        footerTemplate={
          <NgButton
            submitButton
            form={dialogFormId}
            text={t('IncidentArchive_Filters_FilterButtonLabel')}
            onClick={applyFilters}
          />
        }
      />
    </>
  );
}
